import React, { useState, useEffect } from 'react';
import { Save, Search, AlertCircle, CheckCircle, XCircle, BookOpen } from 'lucide-react';
import axios from 'axios';

const MarksEntry = () => {
  const [filters, setFilters] = useState({ 
    course_id: '',
    semester_id: '',
    exam_id: '', 
    subject_id: '' 
  });
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [allSubjects, setAllSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [totalMarks, setTotalMarks] = useState(100);
  const [passingMarks, setPassingMarks] = useState(40);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedSemester, setSelectedSemester] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchCourses();
    fetchSemesters();
    fetchExams();
    fetchSubjects();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourses(response.data);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchSemesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/semesters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSemesters(response.data);
      setSemesters(response.data);
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/examination/exams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/subjects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllSubjects(response.data);
      setSubjects(response.data);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ course_id: courseId, semester_id: '', exam_id: '', subject_id: '' });
    const course = courses.find(c => c.id === courseId);
    setSelectedCourse(course);
    setSelectedSemester(null);
    setSelectedExam(null);
    setSelectedSubject(null);
    
    // Filter semesters by course
    if (courseId) {
      const filteredSemesters = allSemesters.filter(sem => sem.course_id === courseId);
      setSemesters(filteredSemesters);
    } else {
      setSemesters(allSemesters);
    }
    
    setStudents([]);
    setMarks({});
    setAttendance({});
  };

  const handleSemesterChange = (semesterId) => {
    setFilters({ ...filters, semester_id: semesterId, exam_id: '', subject_id: '' });
    const semester = semesters.find(s => s.id === semesterId);
    setSelectedSemester(semester);
    setSelectedExam(null);
    setSelectedSubject(null);
    setStudents([]);
    setMarks({});
    setAttendance({});
  };

  const handleExamChange = (examId) => {
    setFilters({ ...filters, exam_id: examId, subject_id: '' });
    const exam = exams.find(e => e.id === examId);
    setSelectedExam(exam);
    setSelectedSubject(null);
    setStudents([]);
    setMarks({});
    setAttendance({});
  };

  const handleSubjectChange = (subjectId) => {
    setFilters({ ...filters, subject_id: subjectId });
    const subject = subjects.find(s => s.id === subjectId);
    setSelectedSubject(subject);
    setStudents([]);
    setMarks({});
    setAttendance({});
  };

  const loadStudents = async () => {
    if (!filters.course_id || !filters.semester_id || !filters.exam_id || !filters.subject_id) {
      alert('Please select Course, Semester, Exam and Subject');
      return;
    }

    setLoading(true);
    try {
      // Get exam details for total/passing marks
      const examResponse = await axios.get(
        `${API_URL}/api/examination/exams/${filters.exam_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const exam = examResponse.data;
      setTotalMarks(exam.total_marks || 100);
      setPassingMarks(exam.passing_marks || 40);

      // Get students for selected course and semester
      const studentsResponse = await axios.get(`${API_URL}/api/students/dropdown`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const studentsData = Array.isArray(studentsResponse.data) ? studentsResponse.data : (studentsResponse.data?.data || []);
      const filteredStudents = studentsData.filter(
        s => s.course_id === filters.course_id && s.semester_id === filters.semester_id
      );
      
      if (filteredStudents.length === 0) {
        alert('No students found for the selected Course and Semester');
        setLoading(false);
        return;
      }

      // Get attendance for this exam+subject
      const attendanceResponse = await axios.get(
        `${API_URL}/api/examination/attendance/get`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            exam_id: filters.exam_id,
            subject_id: filters.subject_id
          }
        }
      );

      const attendanceMap = {};
      if (attendanceResponse.data.saved) {
        attendanceResponse.data.attendance.forEach(record => {
          attendanceMap[record.student_id] = record.status;
        });
      }
      setAttendance(attendanceMap);

      // Get existing marks
      const marksResponse = await axios.get(
        `${API_URL}/api/examination/marks`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            exam_id: filters.exam_id,
            subject_id: filters.subject_id
          }
        }
      );

      const existingMarks = {};
      const marksData = Array.isArray(marksResponse.data) ? marksResponse.data : (marksResponse.data?.data || []);
      marksData.forEach(record => {
        existingMarks[record.student_id] = record.marks_obtained;
      });
      setMarks(existingMarks);

      setStudents(filteredStudents);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load students: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMarksChange = (studentId, value) => {
    const numValue = parseFloat(value);
    if (value === '' || (numValue >= 0 && numValue <= totalMarks)) {
      setMarks(prev => ({ ...prev, [studentId]: value }));
    }
  };

  const calculateGrade = (obtainedMarks) => {
    if (!obtainedMarks || obtainedMarks === '') return '-';
    const marks = parseFloat(obtainedMarks);
    const percentage = (marks / totalMarks) * 100;
    
    if (marks < passingMarks) return 'F';
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    return 'D';
  };

  const handleSaveMarks = async () => {
    if (!filters.exam_id || !filters.subject_id) {
      alert('Please select Exam and Subject');
      return;
    }

    setSaving(true);
    try {
      const marksData = [];
      
      students.forEach(student => {
        const studentMarks = marks[student.id];
        if (studentMarks !== undefined && studentMarks !== '') {
          marksData.push({
            student_id: student.id,
            exam_id: filters.exam_id,
            subject_id: filters.subject_id,
            marks_obtained: parseFloat(studentMarks),
            total_marks: totalMarks,
            grade: calculateGrade(studentMarks),
            attendance_status: attendance[student.id] || 'Unknown'
          });
        }
      });

      if (marksData.length === 0) {
        alert('Please enter marks for at least one student');
        setSaving(false);
        return;
      }

      // Save each mark entry
      for (const markEntry of marksData) {
        await axios.post(
          `${API_URL}/api/examination/marks`,
          markEntry,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      alert(`✅ Marks saved successfully for ${marksData.length} students!`);
      
    } catch (error) {
      console.error('Error saving marks:', error);
      alert('❌ Failed to save marks: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Filter exams based on selected course and semester
  const filteredExams = exams.filter(exam => 
    (!filters.course_id || exam.course_id === filters.course_id) &&
    (!filters.semester_id || exam.semester_id === filters.semester_id)
  );

  // Filter subjects based on selected course and semester
  const filteredSubjects = allSubjects.filter(sub => 
    (!filters.course_id || sub.course_id === filters.course_id) &&
    (!filters.semester_id || sub.semester_id === filters.semester_id)
  );

  const presentCount = students.filter(s => attendance[s.id] === 'Present').length;
  const absentCount = students.filter(s => attendance[s.id] === 'Absent').length;
  const marksEnteredCount = Object.keys(marks).filter(k => marks[k] !== '').length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Marks Entry - Subject Wise</h1>
          <p className="text-sm text-gray-500 mt-1">Enter marks for each subject (one subject at a time)</p>
          {selectedCourse && selectedSemester && selectedExam && selectedSubject && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                {selectedCourse.name}
              </span>
              <span className="text-gray-400">→</span>
              <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                {selectedSemester.name}
              </span>
              <span className="text-gray-400">→</span>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {selectedExam.name}
              </span>
              <span className="text-gray-400">→</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center gap-1">
                <BookOpen className="h-3 w-3" />
                {selectedSubject.name} ({selectedSubject.code})
              </span>
            </div>
          )}
        </div>
        {students.length > 0 && (
          <button 
            onClick={handleSaveMarks}
            disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save All Marks'}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Students</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{students.length}</p>
            </div>
            <Search className="h-12 w-12 text-blue-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Present</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{presentCount}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Absent</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{absentCount}</p>
            </div>
            <XCircle className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Marks Entered</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{marksEnteredCount}</p>
            </div>
            <Save className="h-12 w-12 text-purple-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Select Course, Semester, Exam & Subject</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.course_id} 
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              <option value="">Select Course</option>
              {courses.map(course => (
                <option key={course.id} value={course.id}>{course.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Semester *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.semester_id} 
              onChange={(e) => handleSemesterChange(e.target.value)}
              disabled={!filters.course_id}
            >
              <option value="">{filters.course_id ? 'Select Semester' : 'Select course first'}</option>
              {semesters.map(sem => (
                <option key={sem.id} value={sem.id}>{sem.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Exam *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.exam_id} 
              onChange={(e) => handleExamChange(e.target.value)}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Exam' : 'Select semester first'}</option>
              {filteredExams.map(exam => (
                <option key={exam.id} value={exam.id}>
                  {exam.name} - {exam.exam_type}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
            <select 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500" 
              value={filters.subject_id} 
              onChange={(e) => handleSubjectChange(e.target.value)}
              disabled={!filters.course_id || !filters.semester_id}
            >
              <option value="">{filters.semester_id ? 'Select Subject' : 'Select semester first'}</option>
              {filteredSubjects.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name} ({sub.code})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button 
              onClick={loadStudents}
              disabled={!filters.course_id || !filters.semester_id || !filters.exam_id || !filters.subject_id || loading}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Loading...' : 'Load Students'}
            </button>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>📘 Subject-wise Marks Entry:</strong> Course → Semester → Exam → Subject → Load Students → Enter Marks → Save
          </p>
        </div>
      </div>

      {students.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 bg-yellow-50 border-b border-yellow-200">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
              <div>
                <p className="text-sm font-semibold text-yellow-800">Attendance Integration Active</p>
                <p className="text-xs text-yellow-700 mt-1">
                  • Green row = Student was Present in exam (can enter marks)
                  <br />
                  • Red row = Student was Absent (marks entry not recommended)
                  <br />
                  • Gray row = Attendance not marked yet
                </p>
              </div>
            </div>
          </div>

          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roll No / Admission No</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Name</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Attendance</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Marks Obtained</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Total Marks</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Grade</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {students.map((student) => {
                const studentAttendance = attendance[student.id];
                const rowClass = studentAttendance === 'Present' 
                  ? 'bg-green-50' 
                  : studentAttendance === 'Absent' 
                  ? 'bg-red-50' 
                  : 'bg-gray-50';
                
                return (
                  <tr key={student.id} className={rowClass}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {student.admission_number || student.roll_number || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{student.full_name}</td>
                    <td className="px-6 py-4 text-center">
                      {studentAttendance === 'Present' ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          ✓ Present
                        </span>
                      ) : studentAttendance === 'Absent' ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          ✗ Absent
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          - Not Marked
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <input
                        type="number"
                        min="0"
                        max={totalMarks}
                        step="0.5"
                        className={`w-24 px-3 py-2 border rounded-lg text-center ${
                          studentAttendance === 'Absent' ? 'bg-red-100 border-red-300' : ''
                        }`}
                        value={marks[student.id] || ''}
                        onChange={(e) => handleMarksChange(student.id, e.target.value)}
                        placeholder="0"
                        disabled={studentAttendance === 'Absent'}
                      />
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-900">{totalMarks}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        calculateGrade(marks[student.id]) === 'F' ? 'bg-red-100 text-red-800' :
                        calculateGrade(marks[student.id]) === 'A+' || calculateGrade(marks[student.id]) === 'A' ? 'bg-green-100 text-green-800' :
                        calculateGrade(marks[student.id]) === '-' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {calculateGrade(marks[student.id])}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Search className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Select Course, Semester, Exam and Subject to load students and enter marks</p>
        </div>
      )}
    </div>
  );
};

export default MarksEntry;